import { type ApplicationFormSettings, type Api, type Fair, type FairDto } from '@/api'
import { defineStore } from 'pinia'
import { computed, inject, ref } from 'vue'
import { useSiteStore } from './site'
import { useExhibitorStore } from './exhibitor'
import { clone } from 'lodash'
import { useI18n } from 'vue-i18n'
import { useCompanyStore } from './company'

export const useFairStore = defineStore('fairStore', () => {
  const fair = ref<Fair>()
  const fairs = ref<FairDto[]>([])
  const api = inject('exhibitorApi') as Api<unknown>
  const { locale, t } = useI18n()
  const siteStore = useSiteStore()
  const exhibitorStore = useExhibitorStore()
  const companyStore = useCompanyStore()

  const lastFairId = computed({
    get: () => parseInt(localStorage.getItem('currentFairId') ?? '0'),
    set: (newFairId: number) => {
      localStorage.setItem('currentFairId', newFairId.toString())
    }
  })

  const defaultApplicationFormSettings: ApplicationFormSettings = {
    useInvitation: false,
    isRequireFairTel: false,
    isRequireFairFax: false,
    isRequireTaxTel: false,
    isRequireTaxFax: false,
    isRequireAddStaff: false,
    isRequireOnlineStaff: false,
    isRequireOnlineStaffTel: false,
    isRequireOnlineStaffFax: false,
    isRequireOnsiteStaff: false,
    isRequireOnsiteStaffTel: false,
    isRequireOnsiteStaffFax: false,
    isRequireAddInfo: false,
    invitationMaxCount: 0,
    invitationUnitCount: 0,
    useExhibitionImage: false,
    exhibitionImageLabel: '출품 이미지',
    useUsdCurrencyOnly: false
  }

  const fairMasterId = computed(() => fair.value?.fairMasterId ?? siteStore.siteConfig.fairMasterId)
  const applicationFormSettings = ref<ApplicationFormSettings>(clone(defaultApplicationFormSettings))

  // 업체가 참가신청이 가능한 기간인지 체크하는 computed 프로퍼티
  const isExhibitorApplicablePeriod = computed(() => {
    const fairDto = fairs.value.find((f) => f.id === fair.value?.id)
    if (!fairDto) {
      return false
    } else if (fairDto.isTestable) {
      return true
    }

    if (!fair.value?.reqStartDate || !fair.value?.reqEndDate) {
      return false
    }

    const now = new Date()
    const startDate = new Date(fair.value.reqStartDate)
    const endDate = new Date(fair.value.reqEndDate)
    return now >= startDate && now <= endDate
  })

  /**
   * 현재 관리 중인 전시회를 변경합니다.
   * 목록에 없는 전시회로는 변경하지 않습니다
   *
   * @param fairId 전시회 식별자
   */
  const changeCurrentFair = async (fairId: number) => {
    if (fairs.value.some((f) => f.id === fairId)) {
      const oldFairId = lastFairId.value
      const response = await api.fair.fairDetail(fairId)
      fair.value = response.data
      lastFairId.value = fairId

      await api.fair
        .applicationFormSettingsDetail(fair.value.fairMasterId as number, fair.value.id as number)
        .then((res) => (applicationFormSettings.value = res.data))

      const isDevelopment = import.meta.env.DEV

      if (!isDevelopment) {
        if (applicationFormSettings.value.useUsdCurrencyOnly ?? false) {
          await siteStore.changeLocale('en')
        } else {
          await siteStore.changeLocale(companyStore.company.isOverseaCompany ? 'en' : 'ko')
        }
      }

      if (oldFairId !== lastFairId.value) {
        common.toast({
          severity: 'warn',
          summary: t('fairChangeNotification.summary'),
          detail: t('fairChangeNotification.detail', { fairName: fairName.value })
        })
      }
    }

    await exhibitorStore.refreshExhibitor(fair.value?.id ?? 0)
    await siteStore.refreshMenu()
  }

  const refreshFairs = async () => {
    const response = await api.fair.listByFairMasterIdDetail(fairMasterId.value)
    fairs.value = response.data ?? []
  }

  const fairName = computed(() => (locale.value === 'ko' ? fair.value?.name : fair.value?.nameEng))

  return {
    defaultApplicationFormSettings,
    fair,
    fairName,
    fairs,
    lastFairId,
    fairMasterId,
    isExhibitorApplicablePeriod,
    applicationFormSettings,
    changeCurrentFair,
    refreshFairs
  }
})
